import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (productLink?.href) {
      const url = new URL(productLink.href);
      if (url.searchParams.has('variant')) return url.toString();
      const selectedVariantId = this.#getSelectedVariantId();
      if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);
      return url.toString();
    }

    const dataUrl = this.dataset.productUrl;
    if (dataUrl) return dataUrl;
    return '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const button =
      event.target && typeof event.target.closest === 'function'
        ? event.target.closest('button.quick-add__button--cta-bar')
        : null;

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    try {
      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
      }

      this.#openQuickAddModal();
    } finally {
      if (button && typeof window.ButtonLoading?.stop === 'function') {
        window.ButtonLoading.stop(button);
      }
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    this.#sanitizeProductGridForQuickAdd(productGrid);

    if (isMobileBreakpoint()) {
      this.#buildMobileCompactHeader(productGrid);
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    if (isMobileBreakpoint()) {
      this.#placeMobileCloseButton();
    }
  }

  /**
   * Mobile: miniatura + título/preço na mesma linha; close vai para a row do título depois do morph.
   * @param {Element} productGrid
   */
  #buildMobileCompactHeader(productGrid) {
    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const mediaColumn = productGrid.querySelector('.product-information__media-column');
    const mediaHost = productGrid.querySelector('.product-information__media');
    const media = mediaHost?.querySelector('.quick-add-modal__media');

    const productTitle = document.createElement('a');
    productTitle.className = 'product-header__title';
    productTitle.textContent = this.dataset.productTitle || '';
    productTitle.href = this.productPageUrl;

    const titleRow = document.createElement('div');
    titleRow.className = 'product-header__title-row';
    titleRow.appendChild(productTitle);

    const info = document.createElement('div');
    info.className = 'product-header__info';
    info.appendChild(titleRow);
    if (productPrice) {
      info.appendChild(productPrice);
    }

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    if (media) {
      productHeader.appendChild(media);
    }
    productHeader.appendChild(info);

    mediaHost?.remove();
    mediaColumn?.remove();
    productGrid.prepend(productHeader);

    if (variantPicker) {
      productGrid.appendChild(variantPicker);
    }
    if (productFormComponent) {
      productGrid.appendChild(productFormComponent);
    }

    productDetails?.remove();
  }

  /**
   * Move o botão close para a linha do título no mobile.
   */
  #placeMobileCloseButton() {
    const dialog = document.querySelector('#quick-add-dialog dialog');
    const closeBtn = dialog?.querySelector(':scope > .quick-add-modal__close');
    const titleRow = document.querySelector('#quick-add-modal-content .product-header__title-row');
    if (closeBtn && titleRow) {
      titleRow.appendChild(closeBtn);
    }
  }

  /**
   * Quick buy: layout simples — 1 imagem (cor atual) + título/variantes/quantidade/ATC.
   * @param {Element} productGrid
   */
  #sanitizeProductGridForQuickAdd(productGrid) {
    productGrid.querySelectorAll('.product-wishlist-button-wrap, .st-wishlist-button').forEach((el) => el.remove());
    productGrid.querySelectorAll('.product-media-with-benefits__sidebar, .product-media-benefits').forEach((el) => {
      el.remove();
    });
    productGrid.querySelectorAll('.product-information__details-below').forEach((el) => el.remove());
    productGrid
      .querySelectorAll(
        '.variant-option__size-guide-trigger, [data-size-guide-trigger], [id^="size-guide-dialog"], .size-guide-dialog'
      )
      .forEach((el) => el.remove());

    this.#replaceMediaWithSingleImage(productGrid);
  }

  /**
   * Substitui a galeria por uma única imagem (primeira da cor/filtro atual).
   * @param {Element} productGrid
   */
  #replaceMediaWithSingleImage(productGrid) {
    const mediaHost = productGrid.querySelector('.product-information__media');
    if (!(mediaHost instanceof HTMLElement)) return;

    const sourceImg =
      mediaHost.querySelector('slideshow-slide[aria-hidden="false"] img') ||
      mediaHost.querySelector('slideshow-slide:first-of-type img') ||
      mediaHost.querySelector('.product-media img') ||
      mediaHost.querySelector('img');

    const media = document.createElement('div');
    media.className = 'quick-add-modal__media';

    if (sourceImg instanceof HTMLImageElement) {
      const img = document.createElement('img');
      img.className = 'quick-add-modal__media-image';
      img.src = sourceImg.currentSrc || sourceImg.src;
      img.alt = sourceImg.alt || '';
      img.loading = 'eager';
      img.decoding = 'async';
      if (sourceImg.srcset) img.srcset = sourceImg.srcset;
      img.sizes = '(min-width: 750px) 42vw, 72px';
      media.appendChild(img);
    }

    mediaHost.replaceChildren(media);

    const mediaColumn = productGrid.querySelector('.product-information__media-column');
    mediaColumn?.querySelectorAll('.product-information__details-below').forEach((el) => el.remove());
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleVariantUpdate = (/** @type {CustomEvent} */ event) => {
    this.#updateProductTitleLink(event);
    this.#updateMediaImage(event);
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #updateMediaImage = (/** @type {CustomEvent} */ event) => {
    const html = event.detail?.data?.html;
    if (!(html instanceof Document || html instanceof Element)) return;

    const sourceImg =
      html.querySelector('slideshow-slide[aria-hidden="false"] img') ||
      html.querySelector('slideshow-slide:first-of-type img') ||
      html.querySelector('.product-media img') ||
      html.querySelector('.product-information__media img');

    const currentImg = this.querySelector('.quick-add-modal__media-image');
    if (!(sourceImg instanceof HTMLImageElement) || !(currentImg instanceof HTMLImageElement)) return;

    currentImg.src = sourceImg.currentSrc || sourceImg.src;
    if (sourceImg.srcset) currentImg.srcset = sourceImg.srcset;
    currentImg.alt = sourceImg.alt || currentImg.alt;
  };

  #handleDialogClose = () => {
    this.#restoreCloseButton();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  /**
   * Garante que o close volta para o dialog (não fica preso no conteúdo do morph).
   */
  #restoreCloseButton() {
    const dialog = this.querySelector('dialog');
    const closeBtn = this.querySelector('.quick-add-modal__close');
    if (!dialog || !closeBtn) return;
    if (closeBtn.parentElement === dialog) return;
    dialog.insertBefore(closeBtn, dialog.firstChild);
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
